"use client";
import { useState } from 'react';
import Image from 'next/image';

const WeWork = ({ data }) => {
    const [hoveredSector, setHoveredSector] = useState(null);

    // Shared styles for the cards - optimized for performance
    const cardBase = "relative group overflow-hidden rounded-3xl shadow-md focus:outline-none focus:ring-2 focus:ring-[#4F7AC2] will-change-transform";

    return (
        <section className="py-8 md:py-18 relative overflow-hidden bg-white" aria-labelledby="wework-title">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                
                {/* Heading Section */}
                <div className="flex flex-col items-center gap-4 mb-16">
                    <h2 id="wework-title" className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight text-center tracking-tight">
                        {data.title}
                    </h2>
                    <p className="text-base md:text-lg text-center max-w-3xl text-gray-600 leading-relaxed">
                        {data.description}
                    </p>
                    <div className="w-20 border-b-4 border-[#4F7AC2] mt-2 rounded-full" aria-hidden="true" />
                </div>

                {/* Bento Grid - Exact Layout Preserved */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-4 md:gap-6 auto-rows-fr">
                    
                    {/* First Sector - Top Left Large (Col 4, Row 2) */}
                    <article
                        className={`${cardBase} md:col-span-4 md:row-span-2 aspect-[4/3] md:aspect-auto`}
                        onMouseEnter={() => setHoveredSector(0)}
                        onMouseLeave={() => setHoveredSector(null)}
                        tabIndex={0}
                    >
                        <Image
                            src={data.workCards[0].imageUrl}
                            alt={data.workCards[0].heading}
                            fill
                            priority
                            fetchPriority="high"
                            className="object-cover transition-transform duration-700 group-hover:scale-105"
                            sizes="(max-width: 768px) 100vw, 66vw"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" aria-hidden="true" />

                        <div className="relative h-full p-6 md:p-10 flex flex-col justify-end md:justify-between text-white">
                            <div className="hidden md:block">
                                <span className="text-[10px] font-bold tracking-[0.2em] text-blue-300 mb-3 uppercase">
                                    {data.workCards[0].subheading}
                                </span>
                            </div>
                            <div>
                                <h3 className="text-2xl md:text-4xl font-bold mb-3 leading-tight">
                                    {data.workCards[0].heading}
                                </h3>
                                <p className="text-sm md:text-base text-white/80 leading-relaxed max-w-md line-clamp-3 md:line-clamp-none">
                                    {data.workCards[0].description}
                                </p>
                            </div>
                            <div className="mt-4">
                                <span className="inline-block text-[10px] font-bold tracking-wider uppercase bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
                                    {data.workCards[0].addon}
                                </span>
                            </div>
                        </div>
                    </article>

                    {/* Second Sector - Top Right (Col 2, Row 2) */}
                    <article className={`${cardBase} md:col-span-2 md:row-span-2 aspect-[4/3] md:aspect-auto`} tabIndex={0}>
                        <Image 
                            src={data.workCards[1].imageUrl} 
                            alt={data.workCards[1].heading} 
                            fill 
                            className="object-cover group-hover:scale-105 transition-transform duration-700" 
                            sizes="(max-width: 768px) 100vw, 33vw"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" aria-hidden="true" />
                        <div className="relative h-full p-6 flex flex-col justify-end text-white">
                            <h3 className="text-xl md:text-2xl font-bold mb-2">{data.workCards[1].heading}</h3>
                            <p className="text-xs md:text-sm text-white/80 mb-4 line-clamp-2">{data.workCards[1].description}</p>
                            <div>
                                <span className="text-[9px] font-bold bg-white/10 px-3 py-1.5 rounded-full border border-white/20 uppercase tracking-widest">
                                    {data.workCards[1].addon}
                                </span>
                            </div>
                        </div>
                    </article>

                    {/* Third Sector - Bottom Left (Col 2, Row 2) */}
                    <article className={`${cardBase} md:col-span-2 md:row-span-2 aspect-[4/3] md:aspect-auto`} tabIndex={0}>
                        <Image 
                            src={data.workCards[2].imageUrl} 
                            alt={data.workCards[2].heading} 
                            fill 
                            className="object-cover group-hover:scale-105 transition-transform duration-700" 
                            sizes="(max-width: 768px) 100vw, 33vw"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" aria-hidden="true" />
                        <div className="relative h-full p-6 flex flex-col justify-end text-white">
                            <h3 className="text-xl md:text-2xl font-bold mb-2">{data.workCards[2].heading}</h3>
                            <p className="text-xs md:text-sm text-white/80 mb-4 line-clamp-2">{data.workCards[2].description}</p>
                            <div>
                                <span className="text-[9px] font-bold bg-white/10 px-3 py-1.5 rounded-full border border-white/20 uppercase tracking-widest">
                                    {data.workCards[2].addon}
                                </span>
                            </div>
                        </div>
                    </article>

                    {/* Fourth Sector - Bottom Right Large (Col 4, Row 2) */}
                    <article className={`${cardBase} md:col-span-4 md:row-span-2 aspect-[4/3] md:aspect-auto`} tabIndex={0}>
                        <Image 
                            src={data.workCards[3].imageUrl} 
                            alt={data.workCards[3].heading} 
                            fill 
                            className="object-cover group-hover:scale-105 transition-transform duration-700" 
                            sizes="(max-width: 768px) 100vw, 66vw"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" aria-hidden="true" />
                        <div className="relative h-full p-6 md:p-10 flex flex-col justify-end text-white">
                            <h3 className="text-2xl md:text-4xl font-bold mb-3">{data.workCards[3].heading}</h3>
                            <p className="text-sm md:text-base text-white/80 max-w-md line-clamp-3">{data.workCards[3].description}</p>
                            <div className="mt-4">
                                <span className="text-[10px] font-bold bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20 uppercase">
                                    {data.workCards[3].addon}
                                </span>
                            </div>
                        </div>
                    </article>

                </div>
            </div>
        </section>
    );
};

export default WeWork;
